/**
 * AdminAIModelConfig page — /admin/ai-model-config
 *
 * Standalone admin page for AI model configuration.
 * Card 1 (top): Available models from the LiteLLM proxy (read-only reference).
 * Card 2 (bottom): Per-pipeline model assignments (editable, per-row save).
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Bot, ChevronLeft, RefreshCw, Copy, Check, AlertTriangle } from 'lucide-react';
import { adminAPI, type PipelineModelsConfig } from '../services/api';
import { theme } from '../styles/theme';
import { extractApiError } from '../utils/errorMessages';

// ─── Types ────────────────────────────────────────────────────────────────────

const PIPELINE_LABELS: Record<keyof PipelineModelsConfig, string> = {
  dimensions: 'Dimensions',
  dimensions_verification: 'Dimensions Verification',
  material: 'Material',
  part_category: 'Part Category',
  generic: 'Generic',
  analytics: 'Analytics',
};

const PIPELINE_TYPES = Object.keys(PIPELINE_LABELS) as Array<keyof PipelineModelsConfig>;

// ─── AvailableModelsCard ──────────────────────────────────────────────────────

interface AvailableModelsCardProps {
  models: string[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
  isRefetching: boolean;
}

const AvailableModelsCard: React.FC<AvailableModelsCardProps> = ({
  models,
  isLoading,
  isError,
  onRefresh,
  isRefetching,
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (modelId: string) => {
    navigator.clipboard.writeText(modelId).then(() => {
      setCopiedId(modelId);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  return (
    <div className={`${theme.card.background} rounded-xl border ${theme.border.primary} p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className={`text-base font-semibold ${theme.text.primary}`}>
            Available AI Models
          </h2>
          <p className={`text-xs ${theme.text.tertiary} mt-0.5`}>
            Live from LiteLLM proxy — copy a model ID to use it below.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={isRefetching}
          data-testid="refresh-available-models"
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border ${theme.border.primary} ${theme.text.secondary} hover:${theme.text.primary} transition-colors disabled:opacity-50`}
          aria-label="Refresh model list"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading skeletons */}
      {isLoading && (
        <div className="space-y-2" aria-busy="true">
          {[60, 45, 75].map((w) => (
            <div key={w} className="flex items-center gap-3 py-2">
              <div className={`h-4 rounded animate-pulse bg-bg-hover`} style={{ width: `${w}%` }} />
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-qc-error/10 border border-qc-error/30">
          <AlertTriangle className="w-4 h-4 text-qc-error mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-qc-error">
              Could not reach LiteLLM proxy.
            </p>
            <p className={`text-xs ${theme.text.tertiary} mt-0.5`}>
              Check proxy connectivity and your API key configuration.
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && models && models.length === 0 && (
        <p className={`text-sm ${theme.text.tertiary} py-2`}>
          No models found. Check LiteLLM proxy connectivity.
        </p>
      )}

      {/* Model list */}
      {!isLoading && !isError && models && models.length > 0 && (
        <>
          <ul className="divide-y divide-border" role="list">
            {models.map((modelId) => (
              <li key={modelId} className="flex items-center justify-between py-2.5 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2 h-2 rounded-full bg-qc-success shrink-0" aria-hidden="true" />
                  <span className={`font-mono text-sm ${theme.text.primary} truncate`}>{modelId}</span>
                </div>
                <button
                  onClick={() => handleCopy(modelId)}
                  data-testid={`copy-model-id-${modelId}`}
                  className={`shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded ${theme.text.secondary} hover:${theme.text.primary} transition-colors`}
                  aria-label={`Copy model ID ${modelId}`}
                >
                  {copiedId === modelId ? (
                    <><Check className="w-3.5 h-3.5 text-qc-success" /> Copied</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5" /> Copy</>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

// ─── PipelineModelConfigCard ──────────────────────────────────────────────────

interface PipelineModelConfigCardProps {
  currentConfig: PipelineModelsConfig | undefined;
  isLoading: boolean;
  availableModels: string[];
  onSaveRow: (pipelineType: keyof PipelineModelsConfig, modelName: string) => void;
  savingRow: string | null;
  saveError: Record<string, string>;
}

const PipelineModelConfigCard: React.FC<PipelineModelConfigCardProps> = ({
  currentConfig,
  isLoading,
  availableModels,
  onSaveRow,
  savingRow,
  saveError,
}) => {
  const [rowValues, setRowValues] = useState<Partial<PipelineModelsConfig>>({});

  // Sync local state when server data loads
  React.useEffect(() => {
    if (currentConfig) {
      setRowValues(currentConfig);
    }
  }, [currentConfig]);

  const handleChange = (pt: keyof PipelineModelsConfig, value: string) => {
    setRowValues((prev) => ({ ...prev, [pt]: value }));
  };

  const getDisplayValue = (pt: keyof PipelineModelsConfig): string =>
    rowValues[pt] ?? currentConfig?.[pt] ?? '';

  const isUnsaved = (pt: keyof PipelineModelsConfig): boolean =>
    !!currentConfig && getDisplayValue(pt) !== currentConfig[pt];

  const isUnknown = (pt: keyof PipelineModelsConfig): boolean =>
    availableModels.length > 0 &&
    !!getDisplayValue(pt) &&
    !availableModels.includes(getDisplayValue(pt));

  return (
    <div className={`${theme.card.background} rounded-xl border ${theme.border.primary} p-6`}>
      <div className="mb-4">
        <h2 className={`text-base font-semibold ${theme.text.primary}`}>
          Pipeline Model Assignments
        </h2>
        <p className={`text-xs ${theme.text.tertiary} mt-0.5`}>
          Configure which AI model each pipeline uses. Changes take effect on the next job submission.
        </p>
      </div>

      {/* Loading skeletons */}
      {isLoading && (
        <div className="space-y-3" aria-busy="true">
          {PIPELINE_TYPES.map((pt) => (
            <div key={pt} className="flex items-center gap-3">
              <div className="h-4 w-40 rounded animate-pulse bg-bg-hover" />
              <div className="h-9 flex-1 rounded animate-pulse bg-bg-hover" />
              <div className="h-9 w-16 rounded animate-pulse bg-bg-hover" />
            </div>
          ))}
        </div>
      )}

      {/* Config table */}
      {!isLoading && (
        <table className="w-full text-sm" role="table">
          <thead>
            <tr className={`text-left text-xs font-medium ${theme.text.tertiary} uppercase tracking-wide`}>
              <th className="pb-2 pr-4 w-44" scope="col">Pipeline</th>
              <th className="pb-2 pr-2" scope="col">Model ID</th>
              <th className="pb-2 w-16" scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {PIPELINE_TYPES.map((pt) => {
              const value = getDisplayValue(pt);
              const saving = savingRow === pt;
              const error = saveError[pt];
              const unsaved = isUnsaved(pt);
              const unknown = isUnknown(pt);
              const noModels = availableModels.length === 0;

              return (
                <tr key={pt}>
                  <td className="py-2.5 pr-4 align-middle">
                    <div className="flex items-center gap-1.5">
                      {unsaved && (
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-qc-warning shrink-0"
                          aria-label="Unsaved changes"
                          title="Unsaved changes"
                        />
                      )}
                      <span className={`font-mono text-xs ${theme.text.primary} font-medium`}>
                        {PIPELINE_LABELS[pt]}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-2">
                    <div>
                      <select
                        value={value}
                        onChange={(e) => handleChange(pt, e.target.value)}
                        disabled={noModels}
                        data-testid={`model-input-${pt}`}
                        className={`w-full font-mono text-sm px-3 py-1.5 rounded-md border transition-colors ${unknown ? 'border-qc-warning' : theme.border.primary} ${theme.card.background} ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-interactive-primary disabled:opacity-50 disabled:cursor-not-allowed`}
                        aria-label={`Model for ${PIPELINE_LABELS[pt]} pipeline`}
                      >
                        {noModels && value && <option value={value}>{value}</option>}
                        {noModels && !value && <option value="" disabled>Refresh models above first</option>}
                        {!noModels && unknown && (
                          <option value={value}>{value} ⚠ (not in proxy)</option>
                        )}
                        {!noModels && availableModels.map((modelId) => (
                          <option key={modelId} value={modelId}>{modelId}</option>
                        ))}
                      </select>
                      {unknown && (
                        <p
                          data-testid={`model-unknown-warning-${pt}`}
                          className="text-xs text-qc-warning mt-1 flex items-center gap-1"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          This model is not in the current proxy list.
                        </p>
                      )}
                      {error && (
                        <p className="text-xs text-qc-error mt-1">{error}</p>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 align-middle">
                    <button
                      onClick={() => onSaveRow(pt, value)}
                      disabled={saving || !value}
                      data-testid={`save-model-${pt}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label={`Save model for ${PIPELINE_LABELS[pt]} pipeline`}
                    >
                      {saving ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        'Save'
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminAIModelConfig: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<Record<string, string>>({});
  const [saveSuccess, setSaveSuccess] = useState<Record<string, boolean>>({});

  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  // Query 1: available models from LiteLLM proxy
  const modelsQuery = useQuery({
    queryKey: ['admin', 'ai-models'],
    queryFn: () => adminAPI.systemConfig.aiModels.list().then((r) => r.data),
  });

  // Query 2: current pipeline model assignments
  const pipelineQuery = useQuery({
    queryKey: ['admin', 'pipeline-models'],
    queryFn: () => adminAPI.systemConfig.pipelineModels.get().then((r) => r.data),
  });

  // Mutation: save full config (PUT requires all 6 keys)
  const saveMutation = useMutation({
    mutationFn: (data: PipelineModelsConfig) =>
      adminAPI.systemConfig.pipelineModels.update(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pipeline-models'] });
    },
  });

  const handleSaveRow = async (pt: keyof PipelineModelsConfig, newValue: string) => {
    if (!pipelineQuery.data) return;

    const payload: PipelineModelsConfig = {
      ...pipelineQuery.data,
      [pt]: newValue,
    };

    setSavingRow(pt);
    setSaveError((prev) => ({ ...prev, [pt]: '' }));
    setSaveSuccess((prev) => ({ ...prev, [pt]: false }));

    try {
      await saveMutation.mutateAsync(payload);
      setSaveSuccess((prev) => ({ ...prev, [pt]: true }));
      setTimeout(() => setSaveSuccess((prev) => ({ ...prev, [pt]: false })), 1500);
    } catch (err) {
      setSaveError((prev) => ({ ...prev, [pt]: extractApiError(err) || 'Failed to save. Try again.' }));
    } finally {
      setSavingRow(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-6">
        <Link
          to="/admin"
          className={`flex items-center gap-1 text-sm ${theme.text.secondary} hover:${theme.text.primary} transition-colors`}
        >
          <ChevronLeft className="w-4 h-4" />
          Admin
        </Link>
        <span className={`text-sm ${theme.text.tertiary}`}>/</span>
        <span className={`text-sm ${theme.text.primary} font-medium`}>AI Model Configuration</span>
      </div>

      {/* Page header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2 rounded-lg bg-interactive-primary/10">
          <Bot className="w-6 h-6 text-interactive-primary" />
        </div>
        <div>
          <h2 className={`text-2xl font-bold ${theme.text.primary}`}>AI Model Configuration</h2>
          <p className={`text-sm ${theme.text.secondary} mt-0.5`}>
            Configure which AI model each pipeline uses — changes take effect without redeployment.
          </p>
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-6">
        {/* Card 1: Available models (reference) */}
        <section aria-labelledby="available-models-heading">
          <h2
            id="available-models-heading"
            className={`text-sm font-semibold uppercase tracking-wide ${theme.text.tertiary} mb-3`}
          >
            Step 1 — Check available models
          </h2>
          <AvailableModelsCard
            models={modelsQuery.data?.models}
            isLoading={modelsQuery.isLoading}
            isError={modelsQuery.isError}
            onRefresh={() => modelsQuery.refetch()}
            isRefetching={modelsQuery.isFetching && !modelsQuery.isLoading}
          />
        </section>

        {/* Card 2: Pipeline model assignments */}
        <section aria-labelledby="pipeline-models-heading">
          <h2
            id="pipeline-models-heading"
            className={`text-sm font-semibold uppercase tracking-wide ${theme.text.tertiary} mb-3`}
          >
            Step 2 — Assign models to pipelines
          </h2>
          <PipelineModelConfigCard
            currentConfig={pipelineQuery.data}
            isLoading={pipelineQuery.isLoading}
            availableModels={modelsQuery.data?.models ?? []}
            onSaveRow={handleSaveRow}
            savingRow={savingRow}
            saveError={saveError}
          />
        </section>
      </div>
    </div>
  );
};

export default AdminAIModelConfig;
